import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Loader2, Trash2, X } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Iter-294 — Job Alerts CTA on the Jobs category page.
 *
 *  POST   /api/classifieds/saved-searches
 *         body: { q?, region?, category_slug: "jobs", name }
 *         → { id, already_saved: bool }
 *  GET    /api/classifieds/saved-searches
 *         → { saved_searches: [ ... ] }
 *  DELETE /api/classifieds/saved-searches/{id}
 *
 * Behaviour:
 *   • Guests → /login (with return path).
 *   • Authed & no jobs alerts → CTA shows "Get job alerts" (wash pill).
 *   • Authed & ≥1 jobs alert exists → CTA flips to filled "Alerts on"
 *     and clicking opens a manage dialog that lists all existing jobs
 *     alerts (with per-row Remove) plus an inline create form.
 */
export default function JobAlertButton({ presetQuery = "", presetRegion = "", testId = "job-alert-cta" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const savedQ = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/saved-searches");
      return data?.saved_searches || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });

  const jobAlerts = useMemo(() => {
    if (!savedQ.data) return [];
    return savedQ.data.filter((s) => {
      const slug = (s.category_slug || "").toLowerCase();
      return slug === "jobs" || slug.startsWith("jobs/") || slug.startsWith("jobs-");
    });
  }, [savedQ.data]);

  const isOn = jobAlerts.length > 0;
  const busy = !!user && savedQ.isLoading;

  const handleClick = () => {
    if (!user) {
      nav("/login?next=" + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    setOpen(true);
  };

  // Inline style guarantees the filled active variant wins over any
  // Tailwind arbitrary-color utility already emitted by the inactive
  // branch (they have equal specificity).
  const activeStyle = { backgroundColor: "var(--brand-blue)", color: "#fff", borderColor: "var(--brand-blue)" };
  const inactiveClass = "bg-[color:var(--brand-blue-wash)] hover:bg-[color:var(--brand-blue)]/15 text-[color:var(--brand-blue)] border-[color:var(--brand-blue)]/20";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={testId}
        data-alert-state={isOn ? "on" : "off"}
        aria-pressed={isOn}
        style={isOn ? activeStyle : undefined}
        className={`inline-flex items-center gap-2 rounded-full font-bold text-sm px-4 py-2 border transition-colors disabled:opacity-70 ${
          isOn ? "hover:opacity-90" : inactiveClass
        }`}
        title={isOn ? "Manage your job alerts" : "Get emailed when new jobs go live"}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isOn ? (
          <BellRing className="w-4 h-4" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        {isOn ? "Alerts on" : "Get job alerts"}
      </button>
      {open && (
        <JobAlertDialog
          presetQuery={presetQuery}
          presetRegion={presetRegion}
          existingAlerts={jobAlerts}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function JobAlertDialog({ presetQuery, presetRegion, existingAlerts, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [q, setQ] = useState(presetQuery || "");
  const [region, setRegion] = useState(presetRegion || "");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["saved-searches", user?.id] });
    qc.invalidateQueries({ queryKey: ["saved-searches"] });
    qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const nameBits = [];
      if (q.trim()) nameBits.push(q.trim());
      if (region.trim()) nameBits.push(region.trim());
      const name = nameBits.length ? `Job alert: ${nameBits.join(" · ")}` : "Job alert: All jobs";
      const body = { category_slug: "jobs", name };
      if (q.trim()) body.q = q.trim();
      if (region.trim()) body.region = region.trim();
      const { data } = await sharedApi.post("/classifieds/saved-searches", body);
      return data;
    },
    onSuccess: (data) => {
      invalidate();
      if (data?.already_saved) {
        toast.info("You already have this alert — we'll email you on new matches.");
      } else {
        toast.success("Job alert saved. We'll email you when new matches go live.");
      }
      onClose();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't save the alert — please try again."),
  });

  const remove = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/saved-searches/${id}`),
    onSuccess: (_res, id) => {
      invalidate();
      toast.success("Job alert removed.");
      // If that was the last one, close the dialog — nothing left to manage.
      if (existingAlerts.length <= 1) onClose();
      // Otherwise keep the dialog open so the user can remove more.
      void id;
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't remove — please try again."),
  });

  const hasExisting = existingAlerts.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="job-alert-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 md:p-7 max-h-[90vh] overflow-y-auto"
        data-testid="job-alert-dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--brand-blue)]">Job alerts</div>
            <h3 className="mt-1 font-display text-xl font-black text-slate-900">
              {hasExisting ? "Manage your job alerts" : "Get notified about new jobs"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {hasExisting
                ? "We'll email you when a new job matches any of these."
                : "We'll email you when a new job matches — leave a field blank to match everything."}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="job-alert-close" className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {hasExisting && (
          <div className="mt-5 space-y-2" data-testid="job-alert-existing-list">
            {existingAlerts.map((a) => {
              const bits = [];
              if (a.q) bits.push(a.q);
              if (a.region) bits.push(a.region);
              const summary = bits.join(" · ") || "All jobs";
              const busy = remove.isPending && remove.variables === a.id;
              return (
                <div
                  key={a.id}
                  data-testid={`job-alert-existing-row-${a.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-soft bg-slate-50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{a.name || `Job alert: ${summary}`}</div>
                    <div className="text-[11px] text-slate-500 truncate">{summary}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove.mutate(a.id)}
                    disabled={busy}
                    data-testid={`job-alert-remove-${a.id}`}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Remove
                  </button>
                </div>
              );
            })}
            <Link
              to="/account/saved-searches"
              className="block text-right text-[11px] font-bold uppercase tracking-widest text-[color:var(--brand-blue)] hover:underline pt-1"
              data-testid="job-alert-manage-all"
              onClick={onClose}
            >
              Manage all saved searches →
            </Link>
          </div>
        )}

        <div className={hasExisting ? "mt-5 pt-5 border-t border-subtle" : "mt-4"}>
          {hasExisting && (
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Add another alert</div>
          )}
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Keyword (optional)</span>
            <input
              data-testid="job-alert-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="chef, plumber, driver…"
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Region (optional)</span>
            <input
              data-testid="job-alert-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="Auckland, Wellington, Canterbury…"
              className="mt-1 w-full rounded-xl border border-soft px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
            />
          </label>

          <button
            type="submit"
            disabled={save.isPending}
            data-testid="job-alert-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
          >
            {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save alert
          </button>
        </div>
      </form>
    </div>
  );
}
